/** Pure helpers for the optional Google Drive paperwork mirror. */

export const DRIVE_FOLDER_MIME = "application/vnd.google-apps.folder";
export const TURBO_ATTACHMENT_PROP = "turbo_attachment_id";

export type DriveMirroredFile = {
  attachment_id: string;
  file_id: string;
  web_view_link: string;
  name: string;
};

export type DriveDealMirrorMeta = {
  folder_id: string;
  folder_url: string;
  files: DriveMirroredFile[];
};

const ILLEGAL = /[\\/:*?"<>|\u0000-\u001f]/g;

export function sanitizeDriveName(raw: string, fallback: string, max = 120): string {
  const cleaned = raw.replace(ILLEGAL, " ").replace(/\s+/g, " ").trim();
  const base = (cleaned || fallback).slice(0, max).trim();
  return base || fallback.slice(0, max);
}

export function buildDealDriveFolderName(args: {
  displayName: string | null | undefined;
  dealId: string;
}): string {
  const shortId = args.dealId.replace(/-/g, "").slice(0, 8);
  const label = sanitizeDriveName(args.displayName ?? "", `Deal ${shortId}`, 100);
  return `${label} · ${shortId}`;
}

export function parseDriveDealMirror(metadata: unknown): DriveDealMirrorMeta | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const drive = (metadata as { drive?: unknown }).drive;
  if (!drive || typeof drive !== "object" || Array.isArray(drive)) return null;
  const row = drive as Record<string, unknown>;
  const folder_id = typeof row.folder_id === "string" ? row.folder_id.trim() : "";
  const folder_url = typeof row.folder_url === "string" ? row.folder_url.trim() : "";
  if (!folder_id) return null;
  const files: DriveMirroredFile[] = [];
  if (Array.isArray(row.files)) {
    for (const item of row.files) {
      if (!item || typeof item !== "object" || Array.isArray(item)) continue;
      const f = item as Record<string, unknown>;
      const attachment_id = typeof f.attachment_id === "string" ? f.attachment_id.trim() : "";
      const file_id = typeof f.file_id === "string" ? f.file_id.trim() : "";
      if (!attachment_id || !file_id) continue;
      files.push({
        attachment_id,
        file_id,
        web_view_link: typeof f.web_view_link === "string" ? f.web_view_link : "",
        name: typeof f.name === "string" ? f.name : "",
      });
    }
  }
  return {
    folder_id,
    folder_url: folder_url || `https://drive.google.com/drive/folders/${folder_id}`,
    files,
  };
}

export function driveFolderUrlFromDeal(deal: Record<string, unknown> | null | undefined): string | null {
  if (!deal) return null;
  const url = parseDriveDealMirror(deal.metadata)?.folder_url?.trim() ?? "";
  return url || null;
}

export function mergeDriveDealMirror(
  metadata: Record<string, unknown>,
  patch: { folder_id: string; folder_url: string; file?: DriveMirroredFile }
): Record<string, unknown> {
  const prev = parseDriveDealMirror(metadata);
  const files = [...(prev?.files ?? [])];
  if (patch.file) {
    const idx = files.findIndex((f) => f.attachment_id === patch.file!.attachment_id);
    if (idx >= 0) files[idx] = patch.file;
    else files.push(patch.file);
  }
  return {
    ...metadata,
    drive: {
      folder_id: patch.folder_id,
      folder_url: patch.folder_url,
      files,
    },
  };
}

export function escapeDriveQueryValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}
